import { useState } from "react";
import {
  Table, Button, Modal, Form, Input, Select, Space, Tag, Popconfirm,
  message, Tabs, Drawer, DatePicker, InputNumber, Descriptions,
} from "antd";
import { Plus, Wrench, Package, History, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import type { ColumnType } from "antd/es/table";
import {
  useAssetTypes, useAssetTypeCreate, useAssetTypeUpdate, useAssetTypeDelete,
  useAssetList, useAssetDetail, useAssetCreate, useAssetUpdate, useAssetDelete,
  useAssetServiceHistory, useAssetAddService, useExpiringAssets,
} from "../../../hooks/api/assets/hooks";
import { useClientList } from "../../../hooks/api/clients/hooks";

const STATUS_COLOR: Record<string, string> = {
  active: "green",
  inactive: "default",
  decommissioned: "red",
  sold: "blue",
};

const RESULT_COLOR: Record<string, string> = {
  pass: "green",
  fail: "red",
  advisory: "orange",
  completed: "blue",
};

// ── Asset Types Tab ────────────────────────────────────────────────────────────
function AssetTypesTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();

  const { data: res, isLoading } = useAssetTypes();
  const items: any[] = res?.data ?? [];

  const createMut = useAssetTypeCreate({
    onSuccess: () => { msgApi.success("Asset type created"); setModalOpen(false); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });
  const updateMut = useAssetTypeUpdate({
    onSuccess: () => { msgApi.success("Asset type updated"); setModalOpen(false); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });
  const deleteMut = useAssetTypeDelete({
    onSuccess: () => msgApi.success("Asset type deleted"),
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });

  const onFinish = (vals: any) => {
    if (editing) updateMut.mutate({ ...vals, id: editing.id });
    else createMut.mutate(vals);
  };

  const columns: ColumnType<any>[] = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <span className="font-medium text-slate-800">{v}</span> },
    { title: "Description", dataIndex: "description", key: "description", render: (v) => v || "—" },
    {
      title: "Status", dataIndex: "is_active", key: "is_active",
      render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setEditing(r); form.setFieldsValue(r); setModalOpen(true); }}>Edit</Button>
          <Popconfirm title="Delete this asset type?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex justify-end mb-3">
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }}
        >
          Add Asset Type
        </Button>
      </div>
      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={false}
      />
      <Modal
        open={modalOpen}
        title={editing ? "Edit Asset Type" : "New Asset Type"}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending || updateMut.isPending}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          {editing && (
            <Form.Item name="is_active" label="Status">
              <Select>
                <Select.Option value={true}>Active</Select.Option>
                <Select.Option value={false}>Inactive</Select.Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
}

// ── Service History Panel ──────────────────────────────────────────────────────
function ServiceHistoryPanel({ assetId }: { assetId: number }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();

  const { data: res, isLoading } = useAssetServiceHistory(assetId);
  const records: any[] = res?.data ?? [];
  const addMut = useAssetAddService(assetId, {
    onSuccess: () => { msgApi.success("Service record added"); setModalOpen(false); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });

  const columns: ColumnType<any>[] = [
    { title: "Date", dataIndex: "service_date", key: "service_date", render: (v) => dayjs(v).format("DD/MM/YYYY") },
    { title: "Technician", dataIndex: "technician_name", key: "technician_name", render: (v) => v || "—" },
    { title: "Description", dataIndex: "description", key: "description", render: (v) => v || "—" },
    {
      title: "Result", dataIndex: "result", key: "result",
      render: (v) => <Tag color={RESULT_COLOR[v] || "default"}>{v}</Tag>,
    },
    { title: "Next Due", dataIndex: "next_service_due", key: "next_service_due", render: (v) => v ? dayjs(v).format("DD/MM/YYYY") : "—" },
    { title: "Cost", dataIndex: "cost", key: "cost", render: (v) => v ? `£${Number(v).toFixed(2)}` : "—" },
  ];

  return (
    <>
      {ctx}
      <div className="flex justify-end mb-3">
        <Button icon={<Plus size={14} />} type="primary" onClick={() => setModalOpen(true)}>
          Add Service Record
        </Button>
      </div>
      <Table dataSource={records} columns={columns} rowKey="id" loading={isLoading} size="small" pagination={false} />
      <Modal
        open={modalOpen}
        title="Add Service Record"
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        confirmLoading={addMut.isPending}
      >
        <Form form={form} layout="vertical" onFinish={(vals) => addMut.mutate({
          ...vals,
          service_date: vals.service_date?.format("YYYY-MM-DD"),
          next_service_due: vals.next_service_due?.format("YYYY-MM-DD"),
        })}>
          <Form.Item name="service_date" label="Service Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="result" label="Result" initialValue="completed">
            <Select>
              <Select.Option value="pass">Pass</Select.Option>
              <Select.Option value="fail">Fail</Select.Option>
              <Select.Option value="advisory">Advisory</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="next_service_due" label="Next Service Due">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="cost" label="Cost (£)">
            <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

// ── Assets Tab ─────────────────────────────────────────────────────────────────
function AssetsTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const [filterClientId, setFilterClientId] = useState<number | undefined>();
  const [filterTypeId, setFilterTypeId] = useState<number | undefined>();
  const [showExpiring, setShowExpiring] = useState(false);

  const { data: assetRes, isLoading, refetchWithQuery } = useAssetList({ page: 1, limit: 50 });
  const { data: expiringRes, isLoading: expiringLoading } = useExpiringAssets({ days: 30, page: 1, limit: 50 });

  const activeRes = showExpiring ? expiringRes : assetRes;
  const items: any[] = activeRes?.data ?? [];
  const total = activeRes?.extra?.total ?? 0;

  const { data: typesRes } = useAssetTypes();
  const assetTypes: any[] = typesRes?.data ?? [];

  const { data: clientsRes } = useClientList({ page: 1, limit: 500 });
  const clients: any[] = (clientsRes as any)?.data ?? [];

  const { data: detailRes } = useAssetDetail(selected?.id ?? null);
  const assetDetail = detailRes?.data ?? selected;

  const createMut = useAssetCreate({
    onSuccess: () => { msgApi.success("Asset created"); setModalOpen(false); form.resetFields(); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });
  const updateMut = useAssetUpdate({
    onSuccess: () => { msgApi.success("Asset updated"); setModalOpen(false); },
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });
  const deleteMut = useAssetDelete({
    onSuccess: () => msgApi.success("Asset deleted"),
    onError: (e) => msgApi.error(e?.response?.data?.message || "Error"),
  });

  const onFinish = (vals: any) => {
    const payload = {
      ...vals,
      install_date: vals.install_date?.format("YYYY-MM-DD") || null,
      warranty_expiry: vals.warranty_expiry?.format("YYYY-MM-DD") || null,
      next_service_date: vals.next_service_date?.format("YYYY-MM-DD") || null,
      warranty_expiry_date: vals.warranty_expiry_date?.format("YYYY-MM-DD") || null,
      insurance_expiry_date: vals.insurance_expiry_date?.format("YYYY-MM-DD") || null,
    };
    if (editing) updateMut.mutate({ ...payload, id: editing.id });
    else createMut.mutate(payload);
  };

  const today = dayjs().format("YYYY-MM-DD");

  const columns: ColumnType<any>[] = [
    {
      title: "Asset", dataIndex: "name", key: "name",
      render: (v, r) => (
        <button
          className="text-left"
          onClick={() => { setSelected(r); setDrawerOpen(true); }}
        >
          <div className="font-medium text-blue-700 hover:underline">{v}</div>
          <div className="text-xs text-slate-400">{r.asset_type_name}</div>
        </button>
      ),
    },
    { title: "Serial No.", dataIndex: "serial_number", key: "serial_number", render: (v) => v ? <span className="font-mono text-xs">{v}</span> : "—" },
    { title: "Client", dataIndex: "client_name", key: "client_name", render: (v) => v || "—" },
    { title: "Site", dataIndex: "site_name", key: "site_name", render: (v) => v || "—" },
    { title: "Install Date", dataIndex: "install_date", key: "install_date", render: (v) => v ? dayjs(v).format("DD/MM/YYYY") : "—" },
    {
      title: "Next Service", dataIndex: "next_service_date", key: "next_service_date",
      render: (v) => {
        if (!v) return "—";
        const overdue = v < today;
        return (
          <span className={overdue ? "text-red-600 font-semibold flex items-center gap-1" : ""}>
            {overdue && <AlertTriangle size={12} />}
            {dayjs(v).format("DD/MM/YYYY")}
          </span>
        );
      },
    },
    {
      title: "Status", dataIndex: "status", key: "status",
      render: (v) => <Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag>,
    },
    {
      title: "Warranty", dataIndex: "warranty_expiry_date", key: "warranty_expiry_date",
      render: (date: string, record: any) => {
        if (!date) return <span className="text-slate-400">—</span>;
        const status = record.warranty_status;
        const colorMap: Record<string, string> = { expired: "red", expiring_soon: "orange", ok: "green" };
        const color = colorMap[status] || "default";
        return (
          <Space direction="vertical" size={0}>
            <Tag color={color}>{status === "expired" ? "Expired" : status === "expiring_soon" ? "Expiring Soon" : "OK"}</Tag>
            <span className="text-slate-400" style={{ fontSize: 11 }}>{dayjs(date).format("DD/MM/YYYY")}</span>
          </Space>
        );
      },
    },
    {
      title: "Insurance", dataIndex: "insurance_expiry_date", key: "insurance_expiry_date",
      render: (date: string, record: any) => {
        if (!date) return <span className="text-slate-400">—</span>;
        const status = record.insurance_status;
        const colorMap: Record<string, string> = { expired: "red", expiring_soon: "orange", ok: "green" };
        const color = colorMap[status] || "default";
        return (
          <Space direction="vertical" size={0}>
            <Tag color={color}>{status === "expired" ? "Expired" : status === "expiring_soon" ? "Expiring Soon" : "OK"}</Tag>
            <span className="text-slate-400" style={{ fontSize: 11 }}>{dayjs(date).format("DD/MM/YYYY")}</span>
          </Space>
        );
      },
    },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditing(r);
              form.setFieldsValue({
                ...r,
                install_date: r.install_date ? dayjs(r.install_date) : null,
                warranty_expiry: r.warranty_expiry ? dayjs(r.warranty_expiry) : null,
                next_service_date: r.next_service_date ? dayjs(r.next_service_date) : null,
                warranty_expiry_date: r.warranty_expiry_date ? dayjs(r.warranty_expiry_date) : null,
                insurance_expiry_date: r.insurance_expiry_date ? dayjs(r.insurance_expiry_date) : null,
              });
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm title="Delete this asset?" onConfirm={() => deleteMut.mutate(r.id)}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {ctx}
      {/* Filters + Add */}
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <div className="flex gap-2 flex-wrap items-center">
          {!showExpiring && (
            <>
              <Select
                allowClear
                placeholder="Filter by client"
                style={{ width: 180 }}
                onChange={(v) => { setFilterClientId(v); refetchWithQuery({ client_id: v, page: 1 }); }}
              >
                {clients.map((c) => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
              </Select>
              <Select
                allowClear
                placeholder="Filter by type"
                style={{ width: 180 }}
                onChange={(v) => { setFilterTypeId(v); refetchWithQuery({ type_id: v, page: 1 }); }}
              >
                {assetTypes.map((t) => <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>)}
              </Select>
            </>
          )}
          <Button
            type={showExpiring ? "primary" : "default"}
            danger={showExpiring}
            icon={<AlertTriangle size={14} />}
            onClick={() => setShowExpiring((prev) => !prev)}
          >
            {showExpiring ? "Show All Assets" : "Show Expiring"}
          </Button>
        </div>
        <Button
          type="primary"
          icon={<Plus size={14} />}
          onClick={() => { setEditing(null); form.resetFields(); setModalOpen(true); }}
        >
          Add Asset
        </Button>
      </div>

      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={showExpiring ? expiringLoading : isLoading}
        size="small"
        pagination={{ total, pageSize: 50, showSizeChanger: false }}
      />

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        title={editing ? "Edit Asset" : "New Asset"}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending || updateMut.isPending}
        width={640}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="asset_type_id" label="Asset Type" rules={[{ required: true }]} className="col-span-1">
              <Select placeholder="Select type">
                {assetTypes.map((t) => <Select.Option key={t.id} value={t.id}>{t.name}</Select.Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="name" label="Name" rules={[{ required: true }]} className="col-span-1">
              <Input />
            </Form.Item>
            <Form.Item name="client_id" label="Client" className="col-span-1">
              <Select allowClear placeholder="Select client">
                {clients.map((c) => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="serial_number" label="Serial Number" className="col-span-1">
              <Input />
            </Form.Item>
            <Form.Item name="make" label="Make" className="col-span-1">
              <Input />
            </Form.Item>
            <Form.Item name="model" label="Model" className="col-span-1">
              <Input />
            </Form.Item>
            <Form.Item name="install_date" label="Install Date" className="col-span-1">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="next_service_date" label="Next Service Date" className="col-span-1">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="warranty_expiry" label="Warranty Expiry" className="col-span-1">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="warranty_expiry_date" label="Warranty Expiry" className="col-span-1">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="insurance_expiry_date" label="Insurance Expiry" className="col-span-1">
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="status" label="Status" initialValue="active" className="col-span-1">
              <Select>
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="inactive">Inactive</Select.Option>
                <Select.Option value="decommissioned">Decommissioned</Select.Option>
                <Select.Option value="sold">Sold</Select.Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item name="location_description" label="Location Description">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="warranty_notes" label="Warranty Notes">
            <Input.TextArea rows={4} maxLength={1000} />
          </Form.Item>
          <Form.Item name="insurance_notes" label="Insurance Notes">
            <Input.TextArea rows={4} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Asset Detail Drawer */}
      <Drawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelected(null); }}
        title={
          <div className="flex items-center gap-2">
            <Package size={16} className="text-blue-600" />
            <span>{assetDetail?.name ?? "Asset Detail"}</span>
          </div>
        }
        width={560}
      >
        {assetDetail && (
          <>
            <Descriptions bordered size="small" column={2} className="mb-4">
              <Descriptions.Item label="Type">{assetDetail.asset_type_name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[assetDetail.status] || "default"}>{assetDetail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Serial No.">{assetDetail.serial_number || "—"}</Descriptions.Item>
              <Descriptions.Item label="Make / Model">{[assetDetail.make, assetDetail.model].filter(Boolean).join(" / ") || "—"}</Descriptions.Item>
              <Descriptions.Item label="Client">{assetDetail.client_name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Site">{assetDetail.site_name || "—"}</Descriptions.Item>
              <Descriptions.Item label="Install Date">{assetDetail.install_date ? dayjs(assetDetail.install_date).format("DD/MM/YYYY") : "—"}</Descriptions.Item>
              <Descriptions.Item label="Next Service">
                {assetDetail.next_service_date ? (
                  <span className={assetDetail.next_service_date < today ? "text-red-600 font-semibold" : ""}>
                    {dayjs(assetDetail.next_service_date).format("DD/MM/YYYY")}
                  </span>
                ) : "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Warranty Expiry">{assetDetail.warranty_expiry ? dayjs(assetDetail.warranty_expiry).format("DD/MM/YYYY") : "—"}</Descriptions.Item>
              <Descriptions.Item label="Location">{assetDetail.location_description || "—"}</Descriptions.Item>
            </Descriptions>
            {assetDetail.notes && (
              <div className="mb-4 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                <div className="font-semibold text-slate-700 mb-1">Notes</div>
                {assetDetail.notes}
              </div>
            )}
            <div className="flex items-center gap-2 mb-3 font-semibold text-slate-700">
              <History size={15} />
              Service History
            </div>
            <ServiceHistoryPanel assetId={assetDetail.id} />
          </>
        )}
      </Drawer>
    </>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
const AssetsPage = () => {
  return (
    <div className="p-6 min-h-screen">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Wrench size={20} className="text-primary" />
          Assets
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Track equipment, assets and service history</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
        <Tabs
          defaultActiveKey="assets"
          items={[
            {
              key: "assets",
              label: (
                <span className="flex items-center gap-1.5">
                  <Package size={14} />
                  Assets
                </span>
              ),
              children: <AssetsTab />,
            },
            {
              key: "types",
              label: (
                <span className="flex items-center gap-1.5">
                  <Wrench size={14} />
                  Asset Types
                </span>
              ),
              children: <AssetTypesTab />,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default AssetsPage;
